import { useCallback, useEffect, useRef, useState } from 'react';
import { acknowledgeRejectedPrintRun, acknowledgeCompletedPrintRun, getPendingPrintIntent, PRINT_INTENT_CHANGED,
  resumePendingPrintRun, currentPrintingCaller, type PendingPrintIntent } from './printingRequests';
import { useSsoReauth } from '../contexts/SsoReauthContext';
import { showToastInfo } from '../components/ToastNotifications';
import { getErrorMessage, isSsoTemporarilyUnavailable } from '../utils/requestErrors';

export const MCA_COUNT_DELAYED_MESSAGE =
  'Your labels are ready. Sending the label count to Metrc is delayed; RetailID will keep retrying.';

// RID-3178: the print is complete for the user; only the Metrc label count is still on its way.
function notifyMcaCountDelayed() {
  showToastInfo(MCA_COUNT_DELAYED_MESSAGE);
}

export function usePendingPrintRequest(organizationId?: number) {
  const { handleSsoError } = useSsoReauth();
  let callerKey = '';
  try { callerKey = JSON.stringify(currentPrintingCaller()); } catch { callerKey = 'unavailable'; }
  const [intent, setIntent] = useState<PendingPrintIntent | null>(null);
  const [error, setError] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [resuming, setResuming] = useState(false);
  const active = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const currentCaller = useRef(callerKey);
  currentCaller.current = callerKey;
  const currentOrganization = useRef(organizationId);
  currentOrganization.current = organizationId;
  const mounted = useRef(true);
  const refresh = useCallback(() => {
    if (currentOrganization.current !== organizationId || currentCaller.current !== callerKey) return;
    if (!organizationId) { setIntent(null); setError(''); return; }
    try { setIntent(getPendingPrintIntent(organizationId)); setError(''); }
    catch (problem) { setIntent(null); setError(getErrorMessage(problem, 'Unable to read your saved print request.')); }
  }, [organizationId, callerKey]);
  useEffect(() => {
    mounted.current = true; setWaiting(false); setResuming(false); refresh();
    window.addEventListener(PRINT_INTENT_CHANGED, refresh);
    window.addEventListener('storage', refresh);
    return () => { mounted.current = false; controller.current?.abort(); window.removeEventListener(PRINT_INTENT_CHANGED, refresh); window.removeEventListener('storage', refresh); };
  }, [refresh, callerKey]);
  const markWaiting = useCallback(() => { if (mounted.current && currentOrganization.current === organizationId && currentCaller.current === callerKey) setWaiting(true); }, [organizationId, callerKey]);
  const requestOptions = useCallback(() => {
    if (!controller.current || controller.current.signal.aborted) controller.current = new AbortController();
    return { signal: controller.current.signal, onWaiting: markWaiting, onMcaCountDelayed: notifyMcaCountDelayed };
  }, [markWaiting]);
  const resume = useCallback(async () => {
    if (!organizationId || active.current) return;
    active.current = true; setResuming(true); setWaiting(true); setError('');
    let failure = '';
    try { await resumePendingPrintRun(organizationId, requestOptions()); }
    catch (problem) {
      // No usable Metrc sign-in: show the sign-in prompt. The request is kept, so it finishes the same print after signing in.
      if (handleSsoError(problem)) {
        failure = isSsoTemporarilyUnavailable(problem)
          ? 'Metrc sign-in is temporarily unavailable. Resume this print request in a few minutes.'
          : 'Sign in to Metrc, then resume this print request.';
      } else {
        failure = getErrorMessage(problem, 'Your print request is still waiting. Resume it to check again.');
      }
    }
    finally { active.current = false; if (mounted.current && currentOrganization.current === organizationId && currentCaller.current === callerKey) { setResuming(false); refresh(); if (failure) setError(failure); } }
  }, [organizationId, callerKey, requestOptions, refresh, handleSsoError]);
  const accept = useCallback(async (id: number | string) => {
    if (!organizationId) return;
    try { await acknowledgeCompletedPrintRun(organizationId, id); if (mounted.current) refresh(); }
    catch (problem) { if (mounted.current && currentOrganization.current === organizationId && currentCaller.current === callerKey) setError(getErrorMessage(problem, 'Your saved print request is retained.')); }
  }, [organizationId, callerKey, refresh]);
  const acknowledgeRejection = useCallback(async () => {
    if (!organizationId || !intent?.rejection || active.current) return;
    try {
      await acknowledgeRejectedPrintRun(organizationId, intent.requestId);
      if (mounted.current && currentOrganization.current === organizationId && currentCaller.current === callerKey) { setWaiting(false); refresh(); }
    } catch (problem) {
      if (mounted.current && currentOrganization.current === organizationId && currentCaller.current === callerKey) setError(getErrorMessage(problem, 'Your failed print request is retained.'));
    }
  }, [organizationId, callerKey, intent, refresh]);
  return { intent, error, waiting, resuming, blocked: Boolean(intent || error), resume, accept, acknowledgeRejection, markWaiting, requestOptions };
}
